import React, { useState } from 'react';
import { Shield, FileText, Download, Users, X, Lock, AlertCircle, CheckCircle } from 'lucide-react';
import { AuditLogViewer } from './AuditLogViewer';
import { BackupRestoreManager } from './BackupRestoreManager';
import { UserPermissionsManager } from './UserPermissionsManager';
import { useLocalAuth } from '../hooks/useLocalAuth';
import { PasswordSecurityInfo } from './security/PasswordSecurityInfo';

interface SecurityComplianceMenuProps {
  onClose: () => void;
}

export const SecurityComplianceMenu: React.FC<SecurityComplianceMenuProps> = ({ onClose }) => {
  const [showAuditLogs, setShowAuditLogs] = useState(false);
  const [showBackupRestore, setShowBackupRestore] = useState(false);
  const [showUserPermissions, setShowUserPermissions] = useState(false);
  const [showPasswordSecurity, setShowPasswordSecurity] = useState(false);
  const { user } = useLocalAuth();

  const handleShowAuditLogs = () => {
    if (user?.permissions?.canViewAuditLogs) {
      setShowAuditLogs(true);
      onClose();
    }
  };

  const handleShowBackupRestore = () => {
    setShowBackupRestore(true);
    onClose();
  };

  const handleShowUserPermissions = () => {
    if (user?.permissions?.canManagePermissions) {
      setShowUserPermissions(true);
      onClose();
    }
  };

  const handleShowPasswordSecurity = () => {
    setShowPasswordSecurity(true);
    onClose();
  };

  return (
    <>
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
        <div className="bg-white rounded-lg p-6 w-full max-w-md">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-xl font-bold flex items-center gap-2">
              <Shield className="w-6 h-6 text-blue-600" />
              <span>Sicherheit & Compliance</span>
            </h2>
            <button 
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700 transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          <div className="space-y-4">
            <button
              onClick={handleShowPasswordSecurity}
              className="w-full flex items-center gap-3 p-4 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <div className="bg-green-100 p-2 rounded-full">
                <Lock className="w-6 h-6 text-green-600" />
              </div>
              <div className="text-left">
                <h3 className="font-medium text-gray-900">Passwort-Sicherheit</h3>
                <p className="text-sm text-gray-500">Informationen zur Passwort-Sicherheit und -Richtlinien</p>
              </div>
            </button>
            
            <button
              onClick={handleShowAuditLogs}
              disabled={!user?.permissions?.canViewAuditLogs}
              className="w-full flex items-center gap-3 p-4 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <div className="bg-blue-100 p-2 rounded-full">
                <FileText className="w-6 h-6 text-blue-600" />
              </div>
              <div className="text-left">
                <h3 className="font-medium text-gray-900">Audit-Log</h3>
                <p className="text-sm text-gray-500">Vollständige Aufzeichnung aller Änderungen</p>
              </div>
            </button>
            
            <button
              onClick={handleShowBackupRestore}
              className="w-full flex items-center gap-3 p-4 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <div className="bg-green-100 p-2 rounded-full">
                <Download className="w-6 h-6 text-green-600" />
              </div>
              <div className="text-left">
                <h3 className="font-medium text-gray-900">Backup & Wiederherstellung</h3>
                <p className="text-sm text-gray-500">Daten sichern und wiederherstellen</p>
              </div>
            </button>
            
            <button
              onClick={handleShowUserPermissions}
              disabled={!user?.permissions?.canManagePermissions}
              className="w-full flex items-center gap-3 p-4 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <div className="bg-purple-100 p-2 rounded-full">
                <Users className="w-6 h-6 text-purple-600" />
              </div>
              <div className="text-left">
                <h3 className="font-medium text-gray-900">Benutzerberechtigungen</h3>
                <p className="text-sm text-gray-500">Erweiterte Berechtigungsverwaltung</p>
              </div>
            </button>
          </div>
        </div>
      </div>

      {showAuditLogs && (
        <AuditLogViewer onClose={() => setShowAuditLogs(false)} />
      )}

      {showBackupRestore && (
        <BackupRestoreManager onClose={() => setShowBackupRestore(false)} />
      )}

      {showUserPermissions && (
        <UserPermissionsManager onClose={() => setShowUserPermissions(false)} />
      )}

      {showPasswordSecurity && (
        <PasswordSecurityInfo onClose={() => setShowPasswordSecurity(false)} />
      )}
    </>
  );
};